import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as ArchiveIcon,
  EditOutlined as EditIcon,
  InboxOutlined as InboxIcon,
  LinkOutlined as LinkedIcon,
  Search as SearchIcon,
  VisibilityOutlined as ViewIcon,
  WarehouseOutlined as FacilityIcon,
} from '@mui/icons-material';
import FacilityFormDialog from '../../components/facilities/FacilityFormDialog';
import PageHeader from '../../components/PageHeader';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import PaginationControl from '../../components/PaginationControl';
import { APP_SHAPE, elevatedPanelSx } from '../../constants/shape';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { useConfirm } from '../../hooks/useConfirm';
import { useFacilityStore } from '../../stores/facility.store';
import { useOrganizationStore } from '../../stores/organization.store';
import type { Facility, FacilityInput, FacilityStatus, FacilityType } from '../../types/facility.type';

const PAGE_SIZE = 20;

export default function Facilities() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const {
    facilities,
    total,
    isLoading,
    isSaving,
    error,
    clearError,
    fetchFacilities,
    createFacility,
    updateFacility,
    archiveFacility,
  } = useFacilityStore();
  const { canMutate } = useAuthPermissions();
  const { confirm, confirmDialog } = useConfirm();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [type, setType] = useState<'' | FacilityType>('');
  const [status, setStatus] = useState<'' | Exclude<FacilityStatus, 'archived'>>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);

  const orgId = selectedOrganization?.id;
  const canCreate = Boolean(orgId && canMutate(`recyclee.${orgId}.*.facility.create`));
  const canEdit = Boolean(orgId && canMutate(`recyclee.${orgId}.*.facility.write`));
  const canArchive = Boolean(orgId && canMutate(`recyclee.${orgId}.*.facility.delete`));

  useEffect(() => {
    if (!orgId) return;
    void fetchFacilities({
      organization_id: orgId,
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
      type: type || undefined,
      status: status || undefined,
    });
  }, [orgId, page, search, type, status, fetchFacilities]);

  useEffect(() => setPage(1), [orgId, search, type, status]);

  const hasActiveFilters = Boolean(search || type || status);
  const clearFilters = () => {
    setSearch('');
    setType('');
    setStatus('');
  };

  if (!selectedOrganization) {
    return <Alert severity="info">{t('facilities.selectOrganization')}</Alert>;
  }

  const openCreate = () => {
    clearError();
    setEditingFacility(null);
    setFormOpen(true);
  };

  const openEdit = (facility: Facility) => {
    clearError();
    setEditingFacility(facility);
    setFormOpen(true);
  };

  const submitForm = async (input: FacilityInput): Promise<boolean> => {
    if (!orgId) return false;
    const result = editingFacility
      ? await updateFacility(orgId, editingFacility.id, input)
      : await createFacility(orgId, input);
    return Boolean(result);
  };

  const archive = async (facility: Facility) => {
    const confirmed = await confirm({
      title: t('common.archive'),
      description: t('facilities.archiveConfirm', { name: facility.name }),
      tone: 'danger',
    });
    if (confirmed && orgId) await archiveFacility(orgId, facility.id);
  };

  return (
    <Box>
      <PageHeader
        icon={<FacilityIcon />}
        title={t('facilities.title')}
        subtitle={t('facilities.subtitle')}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        actions={canCreate ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            {t('facilities.addFacility')}
          </Button>
        ) : undefined}
      />

      {error && <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: hasActiveFilters ? '2fr 1fr 1fr auto' : '2fr 1fr 1fr' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            label={t('facilities.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: `${APP_SHAPE.radiusSm}px` } }}
          />
          <FormControl size="small">
            <InputLabel>{t('facilities.form.type')}</InputLabel>
            <Select label={t('facilities.form.type')} value={type} onChange={(event) => setType(event.target.value as typeof type)}>
              <MenuItem value="">{t('facilities.allTypes')}</MenuItem>
              <MenuItem value="recycler_facility">{t('facilities.type.recycler_facility')}</MenuItem>
              <MenuItem value="warehouse">{t('facilities.type.warehouse')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>{t('facilities.form.status')}</InputLabel>
            <Select label={t('facilities.form.status')} value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
              <MenuItem value="">{t('facilities.allStatuses')}</MenuItem>
              <MenuItem value="active">{t('facilities.status.active')}</MenuItem>
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button size="small" onClick={clearFilters} sx={{ whiteSpace: 'nowrap' }}>{t('suppliers.clearFilters')}</Button>
          )}
        </Box>
      </Paper>

      {isLoading ? <LoadingSkeleton variant="table" filters={false} columns={6} rows={6} /> : (
        <Paper elevation={2} sx={elevatedPanelSx}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)' }}>
            <Table stickyHeader sx={{ '& .MuiTableCell-head': { py: 1.5, px: 2 }, '& .MuiTableCell-body': { py: 1.75, px: 2 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('facilities.columns.name')}</TableCell>
                  <TableCell>{t('facilities.columns.type')}</TableCell>
                  <TableCell>{t('facilities.columns.city')}</TableCell>
                  <TableCell>{t('facilities.columns.manager')}</TableCell>
                  <TableCell>{t('facilities.columns.status')}</TableCell>
                  <TableCell align="right">{t('suppliers.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {facilities.map((facility) => (
                  <TableRow key={facility.id} hover>
                    <TableCell>
                      <Typography fontWeight={700}>{facility.name}</Typography>
                      {facility.linked_owner_org ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<LinkedIcon fontSize="small" />}
                          label={t('facilities.linked.chip', { org: facility.linked_owner_org.name })}
                          sx={{ mt: 0.5 }}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={t(`facilities.type.${facility.type}`)} />
                    </TableCell>
                    <TableCell>{facility.city || '—'}</TableCell>
                    <TableCell>
                      {facility.manager
                        ? (facility.manager.full_name || facility.manager.username || '—')
                        : <Typography variant="body2" color="text.secondary">{t('facilities.noManager')}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t(`facilities.status.${facility.status}`)}
                        color={facility.status === 'active' ? 'success' : 'default'}
                        variant={facility.status === 'active' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('suppliers.table.view')}>
                        <IconButton aria-label={`${t('suppliers.table.view')} ${facility.name}`} size="small" onClick={() => navigate(`/transportation/facilities/${facility.id}`)} sx={{ color: 'text.secondary', width: 40, height: 40 }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEdit && !facility.linked_facility_id && (
                        <Tooltip title={t('suppliers.table.edit')}>
                          <IconButton aria-label={`${t('suppliers.table.edit')} ${facility.name}`} size="small" onClick={() => openEdit(facility)} sx={{ color: 'primary.main', width: 40, height: 40 }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canArchive && facility.status === 'active' && (
                        <Tooltip title={t('suppliers.table.archive')}>
                          <IconButton aria-label={`${t('suppliers.table.archive')} ${facility.name}`} size="small" color="error" onClick={() => void archive(facility)} sx={{ width: 40, height: 40 }}>
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {facilities.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 8, border: 0 }}>
                      <Stack alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.text.secondary, 0.08), color: 'text.secondary' }}>
                          <InboxIcon fontSize="large" />
                        </Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {hasActiveFilters ? t('facilities.filteredTitle') : t('facilities.emptyTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                          {hasActiveFilters ? t('facilities.filteredHint') : t('facilities.emptyHint')}
                        </Typography>
                        {!hasActiveFilters && canCreate && (
                          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openCreate}>
                            {t('facilities.addFacility')}
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <PaginationControl page={page} limit={PAGE_SIZE} total={total} onPageChange={setPage} />
          </Box>
        </Paper>
      )}

      {orgId && (
        <FacilityFormDialog
          open={formOpen}
          orgId={orgId}
          facility={editingFacility}
          saving={isSaving}
          submitError={error}
          onClose={() => setFormOpen(false)}
          onSubmit={submitForm}
        />
      )}

      {confirmDialog}
    </Box>
  );
}
